import { DateTime } from 'luxon';
import { commonTypes } from '@/reports/types';
import { RepairCost, AutomobileRepair } from '@/reports/types/vehicles';
import { Accordion, Box, Table, Text } from '@/theme/index';
import { capitalizeString, commaFormatCurrency } from '@/utils/index';

interface VehicleServiceRepairsCardProps {
  repair: AutomobileRepair;
}

/**
 * This component displays the details of vehicle service repairs.
 *
 * @component
 * @param {Object} props - Props for the component.
 * @param {AutomobileRepair} props.repair - Repair data to be displayed.
 *
 * @returns {JSX.Element} Rendered VehicleServiceRepairsCard component.
 */
export function VehicleServiceRepairsCard({
  repair,
}: VehicleServiceRepairsCardProps) {
  /**
   * Generates the cost keys for displaying in the accordion.
   *
   * @returns {Array<Array<string>>} An array of label-value pairs.
   */
  const buildCostKeys = () => {
    const costMappings: { key: keyof RepairCost; label: string }[] = [
      { key: 'parts_cost', label: 'Parts cost' },
      { key: 'parts_cost_cap', label: 'Parts cost cap' },
      {
        key: 'repair_emission_parts_cost',
        label: 'Repair emission parts cost',
      },
      { key: 'labor_cost', label: 'Labor cost' },
      { key: 'labor_cost_cap', label: 'Labor cost cap' },
      {
        key: 'repair_emission_labor_cost',
        label: 'Repair emission labor cost',
      },
      { key: 'hourly_labor_rate', label: 'Hourly labor rate' },
      {
        key: 'tampered_repair_components_cost',
        label: 'Tampered repair components cost',
      },
      { key: 'estimated_additional_cost', label: 'Estimated additional cost' },
    ];

    const costKeys = costMappings
      .filter((mapping) => repair.cost && repair.cost[mapping.key])
      .map((mapping) => {
        if (!repair.cost) return;
        return [mapping.label, commaFormatCurrency(repair.cost[mapping.key])];
      })
      .filter(Boolean) as Array<[string, string]>;

    if (repair.labor_hours) {
      costKeys.push(['Labor hours', repair.labor_hours.toString()]);
    }

    return costKeys;
  };

  /**
   * Generates the parts worked on for displaying in the accordion.
   *
   * @returns {Array<Array<string>>} An array of label-value pairs.
   */
  const buildPartsWorkedOn = (
    partsWorkedOn: AutomobileRepair['parts_worked_on']
  ) => {
    if (!partsWorkedOn) return [];
    const partsWorkedOnKeys = partsWorkedOn
      .map((part) => {
        return [capitalizeString(part.part_name), part.condition_state];
      })
      .filter(Boolean) as Array<[string, string]>;
    return partsWorkedOnKeys;
  };

  /**
   * Parses the date to a more readable format.
   *
   * @param {commonTypes.DateTime} date - The date object to be parsed.
   * @returns {string} Formatted date string.
   */
  const parseDate = (date: commonTypes.DateTime) => {
    if (!date.unix_timestamp) return '-';
    return DateTime.fromSeconds(date.unix_timestamp)
      .setZone('America/New_York')
      .toFormat('MM/dd/yyyy');
  };

  const costKeys = buildCostKeys();
  const partsWorkedOn = buildPartsWorkedOn(repair.parts_worked_on);
  const date = repair.repair_date_time
    ? parseDate(repair.repair_date_time)
    : '-';
  return (
    <Box data-testid="vehicle-service-repairs-card">
      <Box display="flex">
        <Text variant="h3" fontWeight="300">
          Repair Reported on
        </Text>
        <Text ml={0.6} variant="h3">
          {date}
        </Text>
        <Text ml={0.6} variant="h3" fontWeight="300">
          in
        </Text>
        <Text ml={0.6} variant="h3">
          {repair.county || '-'}
        </Text>
      </Box>
      {costKeys.length > 0 && (
        <Accordion
          display="Cost"
          children={
            <Table className="striped flipped mobile" rows={costKeys} />
          }
        />
      )}
      {partsWorkedOn.length > 0 && (
        <Accordion
          display="Parts Worked On"
          children={
            <Table className="striped flipped mobile" rows={partsWorkedOn} />
          }
        />
      )}
    </Box>
  );
}
